import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function About() {
  useEffect(() => {
    // Add Schema.org JSON-LD for SEO
    const siteUrl = import.meta.env.VITE_SITE_URL || '';
    
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "ホーム",
          "item": siteUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "会社概要",
          "item": `${siteUrl}/about`
        }
      ]
    };

    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "おかやま行政書士事務所",
      "url": siteUrl,
      "logo": `${siteUrl}/logo.png`,
      "description": "岡山市を拠点に、地域の皆様に信頼される行政書士事務所として、誠実・迅速・確実なサービスを提供しております。",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "○○町1-2-3",
        "addressLocality": "岡山市北区",
        "addressRegion": "岡山県",
        "postalCode": "700-0000",
        "addressCountry": "JP"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 34.6617,
        "longitude": 133.9191
      },
      "telephone": "+81-86-012-3456",
      "faxNumber": "+81-86-012-3457",
      "email": "info@okayama-gyosei.example.com",
      "foundingDate": "2026",
      "numberOfEmployees": 2,
      "founder": {
        "@type": "Person",
        "name": "岡山太郎",
        "jobTitle": "代表行政書士"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    };

    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.text = JSON.stringify(organizationSchema);
    document.head.appendChild(script2);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[500px] bg-gradient-to-r from-blue-900 to-blue-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="w-full max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">会社概要</h1>
              <p className="text-xl text-white/90 leading-relaxed">
                岡山市を拠点に、地域の皆様に信頼される行政書士事務所として、誠実・迅速・確実なサービスを提供しております。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Office Image Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <img
                src="https://static.readdy.ai/image/cf115492d066f9ab249365df72980427/73a81cf148be6cd90d4bfe404cc60a2b.jpeg"
                alt="おかやま行政書士事務所 外観"
                className="w-full h-[500px] object-cover object-top rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                地域に根ざした<br />信頼の行政書士事務所
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                おかやま行政書士事務所は、岡山市北区を拠点に、建設業許可、会社設立、相続手続きなど、幅広い行政手続きをサポートする行政書士事務所です。
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                地域密着型のサービスを提供し、お客様一人ひとりの状況に寄り添った丁寧な対応を心がけております。初回相談は無料ですので、お気軽にご相談ください。
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                「誠実・迅速・確実」をモットーに、岡山市を中心とした地域の皆様の事業と生活をサポートしてまいります。
              </p>
            </div>
          </div>

          {/* Company Information */}
          <div className="bg-gray-50 rounded-2xl p-10 lg:p-12 shadow-xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">事務所情報</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">事務所名</p>
                    <p className="text-lg font-semibold text-gray-900">おかやま行政書士事務所</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">代表者</p>
                    <p className="text-lg font-semibold text-gray-900">行政書士 岡山 太郎</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">所在地</p>
                    <p className="text-lg font-semibold text-gray-900">
                      〒700-0000<br />
                      岡山市北区○○町1-2-3
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">設立</p>
                    <p className="text-lg font-semibold text-gray-900">2026年</p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">電話番号</p>
                    <a href="tel:0860123456" className="text-lg font-semibold text-blue-600 hover:text-blue-700 cursor-pointer whitespace-nowrap">
                      086-012-3456
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-printer-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">FAX</p>
                    <p className="text-lg font-semibold text-gray-900 whitespace-nowrap">086-012-3457</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">営業時間</p>
                    <p className="text-lg font-semibold text-gray-900">9:00 〜 18:00</p>
                    <p className="text-sm text-gray-600 mt-1">定休日：日曜日・祝日</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">従業員数</p>
                    <p className="text-lg font-semibold text-gray-900">2名</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Qualifications and Memberships */}
          <div className="mt-16 bg-white rounded-2xl p-10 lg:p-12 shadow-xl border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">資格・所属団体</h2>
            <div className="max-w-3xl mx-auto space-y-4">
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <i className="ri-shield-check-line text-blue-600 text-2xl mt-1 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">行政書士登録</p>
                  <p className="text-gray-700">岡山県行政書士会所属</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <i className="ri-shield-check-line text-blue-600 text-2xl mt-1 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">日本行政書士会連合会</p>
                  <p className="text-gray-700">正会員</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Access Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">アクセス</h2>
            <p className="text-lg text-gray-600">岡山市北区の好立地、JR岡山駅から徒歩10分</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">交通アクセス</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-train-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-2">電車でお越しの方</p>
                    <p className="text-gray-700">JR岡山駅より徒歩10分</p>
                    <p className="text-sm text-gray-600 mt-1">岡山駅東口から北へ直進</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-car-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-2">お車でお越しの方</p>
                    <p className="text-gray-700">駐車場完備（2台）</p>
                    <p className="text-sm text-gray-600 mt-1">事前にご連絡いただければ確保いたします</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-bus-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-2">バスでお越しの方</p>
                    <p className="text-gray-700">岡山駅前バス停より徒歩8分</p>
                    <p className="text-sm text-gray-600 mt-1">複数路線が利用可能</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-4">
                  <strong className="text-gray-900">所在地：</strong><br />
                  〒700-0000 岡山市北区○○町1-2-3
                </p>
                <div className="flex gap-4">
                  <a
                    href="tel:0860123456"
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-fill text-lg"></i>
                    電話する
                  </a>
                  <a
                    href="/#contact"
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-900 font-semibold rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-mail-fill text-lg"></i>
                    問い合わせ
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3281.8947!2d133.9191!3d34.6617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDM5JzQyLjEiTiAxMzPCsDU1JzA4LjgiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '500px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="おかやま行政書士事務所の地図"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}